import React from "react";

import Typography from "@mui/material/Typography";
import { ZERO_GUID } from "../../configurator/cards/edgeKeys";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InfoIcon from '@mui/icons-material/Info';
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import { Divider } from "@mui/material";

export interface IDecorSelectorInputOption {
    id: string;
    name: string;
    icon?: string;
    tooltip?: string;
}

/**
 * A common component used by property cards to display decor selector fields.
 */
interface IDecorSelectorInputProps {
    value?: string;
    options: IDecorSelectorInputOption[];
    tooltip?: string;
    editable?: boolean;
    readOnly?: boolean;
    notFound?: boolean;
    /** Ha megadva és nem üres, ezek az ID-k kerülnek a lista tetejére, vizuálisan elválasztva. */
    compatibleIds?: string[];
    /**
     * Ha true: amikor a value NINCS az options-ben, automatikusan az első opcióra esik vissza
     * (onChange-dzsel). Board-nál kell (worktop-szűrő kizárhatja a régi value-t → default kell).
     * Ha false (alapértelmezett): sose ír felül konkrét value-t — a szülő felel érte (él-nél kell,
     * mert a value csak átmenetileg hiányozhat az options-ből, és nem szabad ABC-elsőre cserélni).
     */
    autoSelectFallback?: boolean;
    onChange: (newId: string | null) => void;
}

const DecorSelectorInput: React.FC<IDecorSelectorInputProps> = ({ ...props }) => {
    const [selectedId, setSelectedId] = React.useState<string | null>(props.value || null);

    // NEW: sync if values changed from outside
    React.useEffect(() => {
        setSelectedId(props.value || null);
    }, [props.value]);

    const compatibleSet = React.useMemo(() => {
        return new Set(props.compatibleIds?.length ? props.compatibleIds : []);
    }, [props.compatibleIds]);

    const hasCompatibleGroup = compatibleSet.size > 0;

    // Sort options: compatible first (sorted by name), then the rest (sorted by name)
    const sortedOptions = React.useMemo(() => {
        if (!hasCompatibleGroup) return props.options;
        return [...props.options].sort((a, b) => {
            const aCompat = compatibleSet.has(a.id) ? 0 : 1;
            const bCompat = compatibleSet.has(b.id) ? 0 : 1;
            return aCompat - bCompat;
            // if (aCompat !== bCompat) return aCompat - bCompat;
            // return a.name.localeCompare(b.name);
        });
    }, [props.options, compatibleSet, hasCompatibleGroup]);

    // Derive the option object from the id (memoized for perf)
    const selectedOption = React.useMemo(() => {
        return props.options.find(o => o.id === selectedId) ?? null;
    }, [selectedId, props.options]);

    const lastFallbackRef = React.useRef<string | null>(null);
    const prevValueRef = React.useRef<string | null | undefined>(props.value);
    React.useEffect(() => {
        if (selectedOption !== null) {
            lastFallbackRef.current = null;
            return;
        }

        if (props.options.length === 0) return;

        // Ha van konkrét value (nem üres, nem ZERO_GUID), és NEM kértünk auto-fallback-et,
        // akkor sose írunk felül — a value csak átmenetileg hiányozhat az options-ből, a szülő
        // felel a helyességéért (él combobox). Csak akkor fallback-elünk konkrét value mellett,
        // ha az auto-fallback explicit be van kapcsolva (board combobox: worktop-szűrő kizárhatja
        // a régi value-t, ilyenkor kell egy default választás).
        const hasConcreteValue = props.value && props.value !== ZERO_GUID;
        if (hasConcreteValue && !props.autoSelectFallback) {
            return;
        }

        const fallbackId = props.options[0].id;
        if (lastFallbackRef.current === fallbackId) return;

        lastFallbackRef.current = fallbackId;
        setSelectedId(fallbackId);
        props.onChange(fallbackId);
    }, [selectedOption, props.options, props.value]);

    const getGroupKey = (option: IDecorSelectorInputOption) => {
        return compatibleSet.has(option.id) ? "compatible" : "other";
    };

    return (
        <Tooltip title={props.tooltip || ""} arrow>
            {props.readOnly ?
                <Typography variant="subtitle1" component="div">
                    {selectedOption?.name || "N/A"}
                </Typography>
                :
                <Autocomplete
                    // Remount when the grouping mode toggles. MUI (>=7.3.7) keeps the
                    // previous option list mounted during the popup close animation
                    // (`renderedOptions = popupOpen ? groupedOptions : previousGroupedOptionsRef.current`),
                    // but reads `groupBy` fresh. If `groupBy` flips off->on while that
                    // stale list is still flat, MUI runs `option.options.map` on a
                    // non-group item and throws "Cannot read properties of undefined
                    // (reading 'map')". Forcing a fresh instance on toggle resets that
                    // ref and avoids the mismatch.
                    key={hasCompatibleGroup ? "grouped" : "flat"}
                    multiple={false}
                    size="small"
                    fullWidth
                    slotProps={{
                        listbox: {
                            sx: {
                                // Compatible element
                                '& .MuiAutocomplete-option[data-compatible]': {
                                    backgroundColor: '#bdbdbd',
                                },
                                // Selected (aria-selected)
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                    backgroundColor: '#bdbdbd',
                                },
                                // Keyboard focus
                                '& .MuiAutocomplete-option.Mui-focused': {
                                    backgroundColor: '#e0e0e0 !important',
                                },
                                // Selected + focused
                                '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                                    backgroundColor: '#9e9e9e !important',
                                },
                                // Hover
                                '& .MuiAutocomplete-option:hover': {
                                    backgroundColor: '#e0e0e0 !important',
                                },
                            },
                        },
                    }}
                    options={sortedOptions}
                    value={selectedOption}
                    disabled={props.editable === false || props.options.length === 0}
                    onChange={(e, newOption) => {
                        setSelectedId(newOption ? newOption.id : null);
                        props.onChange(newOption ? newOption.id : null);
                    }}
                    getOptionLabel={(option) => option ? option.name : ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    {...(hasCompatibleGroup ? { groupBy: getGroupKey } : {})}
                    renderGroup={hasCompatibleGroup ? (params) => (
                        <Box key={params.key}>
                            {params.group === "other" && (
                                <Divider sx={{ my: 0.5 }} />
                            )}
                            {params.children}
                        </Box>
                    ) : undefined}
                    renderInput={(params) => <TextField
                        {...params}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                startAdornment: (props.notFound &&
                                    <Tooltip title="Ehhez az anyaghoz nincs kezdeti érték megadva" arrow>
                                        <InputAdornment position="start">
                                            <InfoIcon color="warning" />
                                        </InputAdornment>
                                    </Tooltip>
                                )
                            }
                        }}
                    />}
                    renderOption={(optionProps, option) => {
                        const isCompatible = compatibleSet.has(option.id);
                        const isSelected = option.id === selectedId;

                        return (
                            <li
                                {...optionProps}
                                key={option.id}
                                title={option.tooltip || undefined}
                                data-compatible={isCompatible || undefined}
                                data-current={isCompatible && isSelected || undefined}
                            >
                                {option.icon && (
                                    <span style={{ marginRight: '0.5em', display: 'inline-block', width: '1.2em', textAlign: 'center' }}>
                                        {option.icon}
                                    </span>
                                )}
                                {option.name}
                            </li>
                        );
                    }}
                />
            }
        </Tooltip>
    );
}

export default DecorSelectorInput;
